import { SpinnerIconSVG } from 'src/assets/svg';
import { Button } from 'src/components/Buttons/Button';
import Input from 'src/components/FormComponents/Input';
import { CaretIcon, LibraryIcon } from 'src/components/Icons/icons';
import { AppModal } from 'src/components/Modals/AppModal';
import container from 'src/container';
import {
    OrganizationModule,
    OrganizationModuleHistoryEntry,
    OrganizationModuleType,
    isOrganizationModuleEnabled,
} from 'src/domain/organization';
import { getFullName } from 'src/domain/user';
import useSWR, { mutate } from 'swr';
import { useHistory, useParams } from 'react-router-dom';
import { useNotification } from 'src/components/Notification/useNotfication';
import { Section } from 'src/components/Section';
import { format, parseISO } from 'date-fns';
import nb from 'date-fns/locale/nb';
import enUS from 'date-fns/locale/en-US';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { AdminPermission } from 'src/domain/admin';
import { useEnterSupportMode } from 'src/view/hooks/useEnterSupportMode';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';

const {
    cradle: { munduClient },
} = container;

type ModuleToggleRowProps = {
    label: string;
    helperText: string;
    enabled: boolean;
    saving: boolean;
    onToggle: (enabled: boolean) => void;
    history?: OrganizationModuleHistoryEntry[];
    historyTitle?: string;
    children?: React.ReactNode;
};

const ModuleToggleRow = ({
    label,
    helperText,
    enabled,
    saving,
    onToggle,
    history,
    historyTitle,
    children,
}: ModuleToggleRowProps) => {
    const { i18n } = useTranslation();
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const locale = i18n.language.startsWith('en') ? enUS : nb;
    const historyEntries = history ?? [];

    return (
        <div className="mt-6 border-t border-woodyBrown/20 pt-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <p className="font-semibold">{label}</p>
                    <p className="text-sm text-woodyBrown/60">{helperText}</p>
                </div>
                <button
                    type="button"
                    role="switch"
                    aria-checked={enabled}
                    disabled={saving}
                    className="flex items-center gap-2 outline-none focus:outline-none disabled:opacity-50"
                    onClick={() => onToggle(!enabled)}
                >
                    <span
                        className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-150 ${
                            enabled
                                ? 'bg-palmleafGreen'
                                : 'bg-woodyBrown bg-opacity-20'
                        }`}
                    >
                        <span
                            className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all duration-150 ${
                                enabled ? 'left-[22px]' : 'left-0.5'
                            }`}
                        />
                    </span>
                </button>
            </div>
            {children}
            {!!historyEntries.length && (
                <div className="mt-3 text-sm">
                    <button
                        type="button"
                        className="flex items-center gap-2 text-woodyBrown/70 hover:text-woodyBrown outline-none focus:outline-none"
                        onClick={() => setIsHistoryOpen(open => !open)}
                    >
                        <span
                            className={`relative h-2.5 w-4 transition-transform duration-150 ${
                                isHistoryOpen ? '' : '-rotate-90'
                            }`}
                        >
                            <CaretIcon />
                        </span>
                        {historyTitle} ({historyEntries.length})
                    </button>
                    {isHistoryOpen && (
                        <ul className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-woodyBrown/15 divide-y divide-woodyBrown/10">
                            {[...historyEntries]
                                .reverse()
                                .map((entry, index) => (
                                    <li
                                        key={`${entry.date}-${index}`}
                                        className="flex items-center justify-between gap-3 px-4 py-2.5"
                                    >
                                        <span
                                            className={`inline-flex items-center gap-2 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                                entry.enabled
                                                    ? 'bg-palmleafGreen/20 text-woodyBrown'
                                                    : 'bg-woodyBrown/10 text-woodyBrown/70'
                                            }`}
                                        >
                                            <span
                                                className={`h-1.5 w-1.5 rounded-full ${
                                                    entry.enabled
                                                        ? 'bg-palmleafGreen'
                                                        : 'bg-woodyBrown/40'
                                                }`}
                                            />
                                            {entry.enabled
                                                ? i18n.t(
                                                      'admin.forms.createOrganization.calendarModuleHistoryEnabled',
                                                  )
                                                : i18n.t(
                                                      'admin.forms.createOrganization.calendarModuleHistoryDisabled',
                                                  )}
                                        </span>
                                        <span className="text-woodyBrown/60 whitespace-nowrap">
                                            {format(
                                                parseISO(entry.date),
                                                'dd MMM yyyy HH:mm',
                                                { locale },
                                            )}
                                        </span>
                                    </li>
                                ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

type ModuleMemberLimitProps = {
    organizationId: string;
    moduleType: OrganizationModuleType;
    module?: OrganizationModule;
};

const ModuleMemberLimit = ({
    organizationId,
    moduleType,
    module,
}: ModuleMemberLimitProps) => {
    const { t } = useTranslation();
    const notify = useNotification();
    const [isSaving, setIsSaving] = useState(false);
    const [limitInput, setLimitInput] = useState<string | null>(null);
    const currentLimit = module?.memberLimit;
    const currentMemberCount = module?.memberIds?.length ?? 0;
    const limitValue = limitInput ?? currentLimit?.toString() ?? '';
    const isDirty =
        limitInput !== null && limitInput !== (currentLimit?.toString() ?? '');

    const onSave = async () => {
        const trimmed = limitValue.trim();
        const parsed = trimmed === '' ? null : Number(trimmed);

        if (parsed !== null && (!Number.isInteger(parsed) || parsed < 0)) {
            notify(t('admin.orgInfo.memberLimitInvalid'), true);
            return;
        }

        if (parsed !== null && parsed < currentMemberCount) {
            notify(
                t('admin.orgInfo.memberLimitBelowCurrent', {
                    count: currentMemberCount,
                }),
                true,
            );
            return;
        }

        setIsSaving(true);

        try {
            await munduClient.updateOrganizationModule(organizationId, {
                type: moduleType,
                memberLimit: parsed,
            });
            mutate(['orginfo', organizationId]);
            mutate(['organization', organizationId]);
            setLimitInput(null);
            notify(t('admin.notifications.organizationUpdated'));
        } catch (error) {
            if (error instanceof Error) notify(error.message, true);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
            <div>
                <p className="font-semibold text-woodyBrown/80">
                    {t('admin.orgInfo.memberLimitTitle')}
                </p>
                <p className="text-woodyBrown/60">
                    {t('admin.orgInfo.memberLimitHelper', {
                        count: currentMemberCount,
                    })}
                </p>
            </div>
            <div className="flex items-center gap-2">
                <input
                    type="number"
                    min={0}
                    className="text-input w-24"
                    placeholder={t('admin.orgInfo.memberLimitUnlimited')}
                    value={limitValue}
                    disabled={isSaving}
                    onChange={event => setLimitInput(event.currentTarget.value)}
                />
                {isDirty && (
                    <Button
                        title={t('common.confirm')}
                        onClick={onSave}
                        disabled={isSaving}
                        active
                    />
                )}
            </div>
        </div>
    );
};

export const OrgInfo = () => {
    const { id } = useParams<{ id: string }>();
    const notify = useNotification();
    const history = useHistory();
    const { t, i18n } = useTranslation();
    const [deleteConfirmation, setDeleteConfirmation] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isInfoScreenSaving, setIsInfoScreenSaving] = useState(false);
    const [isCalendarModuleSaving, setIsCalendarModuleSaving] = useState(false);
    const [isAnalyticsModuleSaving, setIsAnalyticsModuleSaving] =
        useState(false);
    const [isAppContentModuleSaving, setIsAppContentModuleSaving] =
        useState(false);
    const { enterSupport, isEnteringSupport } = useEnterSupportMode();
    const user = useSignedInUser();
    const locale = i18n.language.startsWith('en') ? enUS : nb;
    const { data } = useSWR([`orginfo`, id], (_, id) =>
        munduClient.getOrganization(id),
    );

    if (!data)
        return (
            <div className="grid justify-center">
                <SpinnerIconSVG className="animate-spin h-8 w-8 text-woodyBrown" />
            </div>
        );

    const onEdit = () => {
        history.push(`/app/admin/organizations/${id}/edit`);
    };
    const canEnterSupport = (
        user as unknown as { adminPermissions?: AdminPermission[] }
    )?.adminPermissions?.includes(AdminPermission.support);

    const onEnterSupport = () => enterSupport(id);
    const deleteConfirmationValue =
        data.organizationDisplayName || data.organizationName;
    const canDelete = deleteConfirmation === deleteConfirmationValue;

    const closeDeleteModal = () => {
        setDeleteConfirmation('');
        setIsDeleteModalOpen(false);
    };

    const infoScreenModule = data.modules?.find(
        module => module.type === OrganizationModuleType.infoScreen,
    );
    const infoScreenEnabled = isOrganizationModuleEnabled(
        data,
        OrganizationModuleType.infoScreen,
    );

    const onToggleInfoScreen = async (enabled: boolean) => {
        setIsInfoScreenSaving(true);

        try {
            await munduClient.updateOrganizationModule(id, {
                type: OrganizationModuleType.infoScreen,
                enabled,
            });
            mutate(['orginfo', id]);
            mutate(['organization', id]);
            mutate('organizations');
            mutate('organizations-b2c');
            notify(t('admin.notifications.organizationUpdated'));
        } catch (error) {
            if (error instanceof Error) notify(error.message, true);
        } finally {
            setIsInfoScreenSaving(false);
        }
    };

    const calendarModule = data.modules?.find(
        module => module.type === OrganizationModuleType.calendar,
    );

    const onToggleCalendarModule = async (enabled: boolean) => {
        setIsCalendarModuleSaving(true);

        try {
            await munduClient.updateOrganizationModule(id, {
                type: OrganizationModuleType.calendar,
                enabled,
            });
            mutate(['orginfo', id]);
            mutate(['organization', id]);
            mutate('organizations');
            mutate('organizations-b2c');
            notify(t('admin.notifications.organizationUpdated'));
        } catch (error) {
            if (error instanceof Error) notify(error.message, true);
        } finally {
            setIsCalendarModuleSaving(false);
        }
    };

    const analyticsModule = data.modules?.find(
        module => module.type === OrganizationModuleType.analytics,
    );

    const onToggleAnalyticsModule = async (enabled: boolean) => {
        setIsAnalyticsModuleSaving(true);

        try {
            await munduClient.updateOrganizationModule(id, {
                type: OrganizationModuleType.analytics,
                enabled,
            });
            mutate(['orginfo', id]);
            mutate(['organization', id]);
            mutate('organizations');
            mutate('organizations-b2c');
            notify(t('admin.notifications.organizationUpdated'));
        } catch (error) {
            if (error instanceof Error) notify(error.message, true);
        } finally {
            setIsAnalyticsModuleSaving(false);
        }
    };

    const appContentModule = data.modules?.find(
        module => module.type === OrganizationModuleType.appContent,
    );
    const appContentEnabled = isOrganizationModuleEnabled(
        data,
        OrganizationModuleType.appContent,
    );

    const onToggleAppContentModule = async (enabled: boolean) => {
        setIsAppContentModuleSaving(true);

        try {
            await munduClient.updateOrganizationModule(id, {
                type: OrganizationModuleType.appContent,
                enabled,
            });
            mutate(['orginfo', id]);
            mutate(['organization', id]);
            mutate('organizations');
            mutate('organizations-b2c');
            notify(t('admin.notifications.organizationUpdated'));
        } catch (error) {
            if (error instanceof Error) notify(error.message, true);
        } finally {
            setIsAppContentModuleSaving(false);
        }
    };

    const onDelete = () => {
        munduClient
            .deleteOrganization(id)
            .then(() => {
                notify(t('admin.notifications.organizationDeleted'));
                mutate('organizations');
                mutate('organizations-b2c');
                closeDeleteModal();
                history.push('/app/admin');
            })
            .catch((error: Error) => notify(error.message, true));
    };
    return (
        <Section title={data.organizationDisplayName}>
            <div className="w-full">
                <p className="flex justify-between">
                    <span className="font-semibold">
                        {t('admin.orgInfo.fields.superUser')}
                    </span>
                    {getFullName(data)}
                </p>
                <p className="flex justify-between">
                    <span className="font-semibold">
                        {t('admin.orgInfo.fields.phone')}
                    </span>
                    {data.phoneNumber}
                </p>
                <p className="flex justify-between">
                    <span className="font-semibold">
                        {t('admin.orgInfo.fields.email')}
                    </span>
                    {data.email}
                </p>
                <p className="flex justify-between">
                    <span className="font-semibold">
                        {t('admin.orgInfo.fields.orgName')}
                    </span>
                    {data.organizationName}
                </p>
                <p className="flex justify-between">
                    <span className="font-semibold">
                        {t('admin.orgInfo.fields.licences')}
                    </span>
                    {data.licence}
                </p>
                <p className="flex justify-between">
                    <span className="font-semibold">
                        {t('admin.orgInfo.fields.created')}
                    </span>
                    {format(parseISO(data.createdDate), 'dd MMM yyyy', {
                        locale,
                    })}
                </p>
                <p className="flex justify-between">
                    <span className="font-semibold">
                        {t('admin.orgInfo.fields.updated')}
                    </span>
                    {data.updatedDate &&
                        format(parseISO(data.updatedDate), 'dd MMM yyyy', {
                            locale,
                        })}
                </p>
                <ModuleToggleRow
                    label={t(
                        'admin.forms.createOrganization.infoScreenEnabled',
                    )}
                    helperText={t(
                        'admin.forms.createOrganization.infoScreenEnabledHelper',
                    )}
                    enabled={infoScreenEnabled}
                    saving={isInfoScreenSaving}
                    onToggle={onToggleInfoScreen}
                    history={infoScreenModule?.history}
                    historyTitle={t(
                        'admin.forms.createOrganization.infoScreenModuleHistoryTitle',
                    )}
                >
                    {infoScreenEnabled && (
                        <ModuleMemberLimit
                            organizationId={id}
                            moduleType={OrganizationModuleType.infoScreen}
                            module={infoScreenModule}
                        />
                    )}
                </ModuleToggleRow>
                <ModuleToggleRow
                    label={t(
                        'admin.forms.createOrganization.calendarModuleEnabled',
                    )}
                    helperText={t(
                        'admin.forms.createOrganization.calendarModuleEnabledHelper',
                    )}
                    enabled={calendarModule?.enabled ?? false}
                    saving={isCalendarModuleSaving}
                    onToggle={onToggleCalendarModule}
                    history={calendarModule?.history}
                    historyTitle={t(
                        'admin.forms.createOrganization.calendarModuleHistoryTitle',
                    )}
                >
                    {calendarModule?.enabled && (
                        <ModuleMemberLimit
                            organizationId={id}
                            moduleType={OrganizationModuleType.calendar}
                            module={calendarModule}
                        />
                    )}
                </ModuleToggleRow>
                <ModuleToggleRow
                    label={t(
                        'admin.forms.createOrganization.analyticsModuleEnabled',
                    )}
                    helperText={t(
                        'admin.forms.createOrganization.analyticsModuleEnabledHelper',
                    )}
                    enabled={analyticsModule?.enabled ?? false}
                    saving={isAnalyticsModuleSaving}
                    onToggle={onToggleAnalyticsModule}
                    history={analyticsModule?.history}
                    historyTitle={t(
                        'admin.forms.createOrganization.analyticsModuleHistoryTitle',
                    )}
                >
                    {analyticsModule?.enabled && (
                        <ModuleMemberLimit
                            organizationId={id}
                            moduleType={OrganizationModuleType.analytics}
                            module={analyticsModule}
                        />
                    )}
                </ModuleToggleRow>
                <ModuleToggleRow
                    label={t(
                        'admin.forms.createOrganization.appContentModuleEnabled',
                    )}
                    helperText={t(
                        'admin.forms.createOrganization.appContentModuleEnabledHelper',
                    )}
                    enabled={appContentEnabled}
                    saving={isAppContentModuleSaving}
                    onToggle={onToggleAppContentModule}
                    history={appContentModule?.history}
                    historyTitle={t(
                        'admin.forms.createOrganization.appContentModuleHistoryTitle',
                    )}
                >
                    {appContentEnabled && (
                        <ModuleMemberLimit
                            organizationId={id}
                            moduleType={OrganizationModuleType.appContent}
                            module={appContentModule}
                        />
                    )}
                </ModuleToggleRow>
                <div className="inline-flex w-full justify-end gap-4 py-6 flex-wrap">
                    {canEnterSupport && (
                        <Button
                            title={
                                isEnteringSupport
                                    ? t('admin.orgInfo.enteringSupportMode')
                                    : t('admin.orgInfo.enterSupportMode')
                            }
                            onClick={onEnterSupport}
                            disabled={isEnteringSupport}
                        />
                    )}
                    <Button
                        title={t('admin.orgInfo.editButton')}
                        onClick={onEdit}
                        active
                    />
                    <Button
                        title={t('admin.orgInfo.deleteButton')}
                        onClick={() => setIsDeleteModalOpen(true)}
                    />
                </div>
            </div>
            <div
                className={`fixed z-100 inset-0 overflow-y-auto bg-black bg-opacity-75 transition-opacity ${
                    !isDeleteModalOpen && 'hidden'
                }`}
            >
                <AppModal
                    show={isDeleteModalOpen}
                    clickOutside={closeDeleteModal}
                >
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-lightPeach rounded-30px px-20 py-16 text-woodyBrown flex flex-col gap-4 w-[min(90vw,520px)]">
                        <p className="text-lg font-semibold">
                            {t('admin.orgInfo.deleteButton')}
                        </p>
                        <Input
                            className="text-input"
                            type="text"
                            onChange={e =>
                                setDeleteConfirmation(e.currentTarget.value)
                            }
                            label={t('common.confirmDelete', {
                                value: deleteConfirmationValue,
                            })}
                            leadingIcon={LibraryIcon}
                        />
                        <div className="mt-4 flex gap-3 text-sm">
                            <Button
                                title={t('common.confirm')}
                                onClick={onDelete}
                                disabled={!canDelete}
                            />
                            <Button
                                title={t('common.cancel')}
                                onClick={closeDeleteModal}
                            />
                        </div>
                    </div>
                </AppModal>
            </div>
        </Section>
    );
};
